/**
 * Reactive store for the user's active microphone selection plus the
 * list of available inputs. Single source of truth — UI components
 * subscribe via `onChange`, and `VoiceConnection` reads
 * `activeMicId` when it opens the mic.
 *
 * Persists the active deviceId to localStorage so the picker remembers
 * the user's choice across sessions.
 */

import {
  type MicPermissionState,
  discoverMics,
  getMicPermissionState,
  loadActiveMicId,
  onDeviceChange,
  saveActiveMicId,
} from "./MicDiscovery";

export interface MicStoreState {
  permission: MicPermissionState;
  available: MediaDeviceInfo[];
  activeMicId: string | null;
  initialized: boolean;
}

type Listener = (state: MicStoreState) => void;

class MicStoreImpl {
  private state: MicStoreState = {
    permission: "prompt",
    available: [],
    activeMicId: loadActiveMicId(),
    initialized: false,
  };

  private listeners = new Set<Listener>();
  private disposeDeviceChange: (() => void) | null = null;

  get snapshot(): MicStoreState {
    return this.state;
  }

  onChange(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * One-shot init: reads the current permission state without prompting,
   * then enumerates whatever the browser will return at this permission
   * level. Call this on app startup; the picker will show "Tap to enable"
   * if permission is "prompt".
   */
  async init(): Promise<void> {
    if (this.state.initialized) return;

    const permission = await getMicPermissionState();
    let available: MediaDeviceInfo[] = [];

    if (permission === "granted") {
      // Already granted — enumerate without re-prompting.
      const devices = await navigator.mediaDevices.enumerateDevices();
      available = devices.filter((d) => d.kind === "audioinput");
    }

    this.update({ permission, available, initialized: true });

    this.disposeDeviceChange = onDeviceChange((mics) => {
      this.update({ available: mics });
      this.reconcileActiveMic();
    });
  }

  /**
   * Triggers the OS prompt + enumerates labelled inputs. Call from a
   * user gesture (button click) — autoplay-style restrictions in
   * browsers will refuse the prompt outside one.
   */
  async requestPermissionAndEnumerate(): Promise<void> {
    const mics = await discoverMics();
    const permission = await getMicPermissionState();
    this.update({ available: mics, permission });
    this.reconcileActiveMic();
  }

  setActiveMic(id: string | null): void {
    saveActiveMicId(id);
    this.update({ activeMicId: id });
  }

  dispose(): void {
    this.disposeDeviceChange?.();
    this.disposeDeviceChange = null;
    this.listeners.clear();
  }

  private update(patch: Partial<MicStoreState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l(this.state);
  }

  /**
   * If the stored activeMicId no longer exists in the available list
   * (USB headset unplugged), fall back to the OS default. Avoids the
   * common "no audio after disconnect" failure mode.
   */
  private reconcileActiveMic(): void {
    const { activeMicId, available } = this.state;
    if (!activeMicId) return;
    if (!available.some((d) => d.deviceId === activeMicId)) {
      this.setActiveMic(null);
    }
  }
}

export const MicStore = new MicStoreImpl();
